/**
 * Tab 4 — "LMS System Overview" (Task 6, plan §5.6, Phase 10B).
 *
 * Read-only. Two endpoints:
 *   GET .../overview/summary/           OverviewSummaryView:1955
 *       {non_simulated_disbursed_count, on_time_count, missing_payment_count, as_of}
 *   GET .../overview/missing_payments/  OverviewMissingPaymentsView:1999
 *       DRF-paginated FLAT rows: one per (loan, missing date)
 *       {loan_id, missing_date, amount_due, lender_customer_id, lender_code}
 *
 * The drill-down is collapsed by default and revealed by clicking the "Missing
 * Payments" card (or its "View Details" button) — the missing-payments endpoint
 * is an O(n)-queries scan per its own docstring, so it is deliberately NOT
 * fetched until the operator asks for it (`enabled: showDetails`).
 *
 * Each `loan_id` is rendered through <LoanIdLink>, which jumps to Tab 1 / Tab 2
 * with that loan pre-selected (see lmsStaffNav.tsx for the mechanism) — the
 * backend intentionally returns the raw id and leaves link-building to us.
 */
import React from 'react';
import DataTable from 'react-data-table-component';
import { useQuery } from '@tanstack/react-query';
import {
    RefreshCcw,
    Wallet,
    CheckCircle2,
    AlertCircle,
    ChevronDown,
    ChevronRight,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import lmsStaffService, {
    MissingPaymentRow,
    getLmsApiErrorMessage,
} from './lmsStaffApi';
import {
    formatAmount,
    formatDate,
    formatDateTime,
    dataTableStyles as customStyles,
} from './formatters';
import { LoanIdLink } from './lmsStaffNav';

export interface OverviewSummaryProps {
    /**
     * can_edit for `overview_disbursement`, passed down by LmsStaffOpsHub.
     * Accepted for prop-contract consistency with the other tabs; Task 6
     * defines no mutating action, so it is unused here.
     */
    canEdit?: boolean;
}

/** One headline number. `onClick` makes the card itself the drill-down trigger. */
const SummaryCard: React.FC<{
    title: string;
    value: number | undefined;
    icon: React.ReactNode;
    valueClassName?: string;
    isLoading: boolean;
    onClick?: () => void;
    footer?: React.ReactNode;
}> = ({ title, value, icon, valueClassName, isLoading, onClick, footer }) => (
    <Card
        className={onClick ? 'cursor-pointer transition-shadow hover:shadow-md' : undefined}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
            onClick
                ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onClick();
                      }
                  }
                : undefined
        }
    >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
            {icon}
        </CardHeader>
        <CardContent>
            {isLoading ? (
                <Skeleton className="h-8 w-24" />
            ) : (
                <div className={`text-3xl font-bold ${valueClassName || 'text-gray-900'}`}>
                    {value ?? '-'}
                </div>
            )}
            {footer && <div className="mt-2">{footer}</div>}
        </CardContent>
    </Card>
);

const OverviewSummary: React.FC<OverviewSummaryProps> = () => {
    const [showDetails, setShowDetails] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(25);

    const {
        data: summary,
        isLoading: summaryLoading,
        isError: summaryError,
        error: summaryErrorObj,
        refetch: refetchSummary,
        isFetching: summaryFetching,
    } = useQuery({
        queryKey: ['lms-staff-overview-summary'],
        queryFn: async () => {
            const response = await lmsStaffService.getOverviewSummary();
            return response.data;
        },
    });

    const {
        data: missing,
        isLoading: missingLoading,
        isError: missingError,
        refetch: refetchMissing,
    } = useQuery({
        queryKey: ['lms-staff-overview-missing-payments', page, pageSize],
        queryFn: async () => {
            const response = await lmsStaffService.getOverviewMissingPayments({
                page,
                page_size: pageSize,
            });
            return response.data;
        },
        // Deliberately deferred — see the module docstring.
        enabled: showDetails,
    });

    const rows: MissingPaymentRow[] = missing?.results ?? [];

    const columns = React.useMemo(
        () => [
            {
                name: 'Loan ID',
                selector: (row: MissingPaymentRow) => row.loan_id,
                sortable: true,
                width: '220px',
                cell: (row: MissingPaymentRow) => <LoanIdLink loanId={row.loan_id} />,
            },
            {
                name: 'Missing Date',
                selector: (row: MissingPaymentRow) => row.missing_date || '',
                sortable: true,
                width: '150px',
                cell: (row: MissingPaymentRow) => formatDate(row.missing_date),
            },
            {
                name: 'Amount Due',
                selector: (row: MissingPaymentRow) => row.amount_due ?? 0,
                sortable: true,
                width: '150px',
                cell: (row: MissingPaymentRow) => formatAmount(row.amount_due),
            },
            {
                name: 'Customer',
                selector: (row: MissingPaymentRow) => row.lender_customer_id || '-',
                sortable: true,
            },
            {
                name: 'Lender',
                selector: (row: MissingPaymentRow) => row.lender_code || '-',
                sortable: true,
                width: '120px',
            },
        ],
        []
    );

    const toggleDetails = () => setShowDetails((current) => !current);

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">LMS System Overview</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Non-simulated disbursed loans (loan IDs not prefixed with
                        &quot;SIM_&quot;)
                        {summary?.as_of ? ` · as of ${formatDateTime(summary.as_of)}` : ''}
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                        refetchSummary();
                        if (showDetails) refetchMissing();
                    }}
                >
                    <RefreshCcw className="h-4 w-4" /> Refresh
                </Button>
            </div>

            {summaryError && (
                <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                    {getLmsApiErrorMessage(
                        summaryErrorObj,
                        'Unable to load the LMS system overview.'
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <SummaryCard
                    title="Non-Simulated Disbursed Loans"
                    value={summary?.non_simulated_disbursed_count}
                    icon={<Wallet className="h-4 w-4 text-gray-400" />}
                    isLoading={summaryLoading}
                />
                <SummaryCard
                    title="On-Time"
                    value={summary?.on_time_count}
                    icon={<CheckCircle2 className="h-4 w-4 text-green-500" />}
                    valueClassName="text-green-600"
                    isLoading={summaryLoading}
                />
                <SummaryCard
                    title="Missing Payments"
                    value={summary?.missing_payment_count}
                    icon={<AlertCircle className="h-4 w-4 text-red-500" />}
                    valueClassName="text-red-600"
                    isLoading={summaryLoading}
                    onClick={toggleDetails}
                    footer={
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-auto gap-1 p-0 text-xs text-intellend-green hover:bg-transparent"
                            onClick={(e) => {
                                // The Card itself is clickable; stop the bubble so
                                // the toggle does not fire twice and cancel out.
                                e.stopPropagation();
                                toggleDetails();
                            }}
                        >
                            {showDetails ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                            )}
                            {showDetails ? 'Hide Details' : 'View Details'}
                        </Button>
                    }
                />
            </div>

            {summaryFetching && !summaryLoading && (
                <p className="text-xs text-gray-400">Refreshing...</p>
            )}

            {showDetails && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-3">
                        <h3 className="text-sm font-semibold text-gray-800">
                            Missing Payments Detail
                        </h3>
                        <p className="mt-1 text-xs text-gray-500">
                            One row per loan per missed installment date. Click a loan ID to
                            open it in the Loan Profile tab (or the pencil icon for Mandate
                            &amp; Payments).
                        </p>
                    </div>

                    {missingError ? (
                        <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                            Unable to load the missing-payments drill-down.
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200">
                            <DataTable
                                columns={columns}
                                data={rows}
                                pagination
                                paginationServer
                                paginationTotalRows={missing?.count ?? 0}
                                paginationDefaultPage={page}
                                onChangePage={(newPage: number) => setPage(newPage)}
                                onChangeRowsPerPage={(
                                    newPerPage: number,
                                    newPage: number
                                ) => {
                                    setPageSize(newPerPage);
                                    setPage(newPage);
                                }}
                                paginationPerPage={pageSize}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                progressPending={missingLoading}
                                progressComponent={
                                    <div className="py-16 text-center text-sm text-gray-500">
                                        Loading missing payments...
                                    </div>
                                }
                                noDataComponent={
                                    <div className="py-16 text-center text-sm text-gray-500">
                                        No missing payments — every non-simulated loan is
                                        on time.
                                    </div>
                                }
                                customStyles={customStyles}
                                highlightOnHover
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OverviewSummary;
